/**
 * 목록의 **페이지가 URL 에 산다.**
 *
 * ─── 왜 여기인가 (2026-09-16, 0120) ───
 * 전역 규칙 13 과 DESIGN.md 가 같은 문장을 적어 뒀다 — *"목록의 검색·필터·페이지를 URL 에
 * 둔다"*(새로고침·뒤로가기·링크 공유). 그런데 그것을 실제로 지키던 화면이 **하나**였고
 * (직원 명부), 나머지는 `useState` 이거나 페이저가 아예 없었다. 이유는 규율이 아니라 **닿을
 * 수 없음**이었다: 판정 함수 둘이 `core-web` 의 화면 폴더 안에 있어서 vertical 이 못 봤다.
 *
 * `web-sdk` 는 `Link`·`useNavigate` 를 **재export 하지 않는다**(`navigation.tsx` 머리) —
 * 그것들은 경로 문자열을 받고, vertical 코드에 경로가 박히면 라우트 파생이 무의미해지기
 * 때문이다. 같은 이유로 `useSearchParams` 도 그대로 열지 않는다. 대신 **계약을 가진 훅**이
 * 나간다: 파라미터 이름·정규화·되돌리기 규칙이 여기 한 곳에 있다.
 *
 * 순수 판정(`toOffset`·`isOutOfRange`)은 `list-range.ts` 다 — DOM 없이 검사한다.
 */
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { createMemoryRouter, RouterProvider, useSearchParams } from 'react-router';
import { DEFAULT_OFFSET_PARAM, toOffset } from './list-range.js';

export interface ListOffsetOptions {
  /** URL 파라미터 이름. 한 화면에 목록이 둘일 때만 바꾼다. */
  readonly param?: string;
  /**
   * 질의를 식별하는 문자열(상태 필터·검색어·날짜…). **바뀌면 첫 페이지로 돌아간다.**
   *
   * 안 돌리면 "상태를 바꿨는데 빈 화면"이 나온다 — 3페이지를 보다가 결과가 12건인 필터로
   * 옮기면 offset 이 그대로 남아 범위 밖이 되고, 그 화면은 "이 상태에는 아무것도 없다"와
   * 구분되지 않는다. 되돌릴 때는 **`replace`** 다: 필터를 세 번 바꾼 사람이 뒤로가기를
   * 세 번 눌러야 할 이유가 없다.
   */
  readonly resetKey?: string;
  /**
   * 페이지 크기도 같은 주소에 둔다(2026-09-18, 0143). **주면** 반환값에 `size`·`setSize`
   * 가 붙는다.
   *
   * ★ **왜 여기 있나 — 따로 훅을 두면 못 고치는 버그가 생긴다.** `offset` 과 크기를
   *   *다른* `useSearchParams()` 호출 둘로 관리하면, "크기를 바꾸고 첫 페이지로도
   *   돌아간다"가 같은 이벤트 핸들러 안에서 `setParams` 를 **두 번** 부르는 일이 된다.
   *   `react-router` 의 `setSearchParams` 는 그 호출 시점의 `location.search` 를
   *   기준으로 다음 주소를 만드는데, 두 번째 호출이 첫 번째가 아직 반영되지 않은
   *   **같은 스냅숏**에서 시작해 그 변경을 덮어쓴다 — 실측(2026-09-18, 브라우저에서
   *   드롭다운을 실제로 바꿔 보고 URL 이 그대로인 것을 봤다). 한 `setParams` 호출
   *   안에서 둘 다 고치면 그 경쟁이 애초에 없다.
   */
  readonly pageSize?: {
    /** URL 파라미터 이름. 기본은 `<param>Size`(offset 파라미터가 여럿이면 크기도 그렇다). */
    readonly param?: string;
    readonly options: readonly number[];
    readonly defaultSize: number;
  };
}

export interface ListOffset {
  readonly offset: number;
  readonly goTo: (next: number) => void;
  /** `pageSize` 를 줬을 때만 있다 — 안 주면 `undefined`. */
  readonly size?: number;
  /** `pageSize` 를 줬을 때만 있다. 첫 페이지로도 같이 돌아간다(`goTo(0)` 과 원자적으로). */
  readonly setSize?: (next: number) => void;
}

export function useListOffset(options: ListOffsetOptions = {}): ListOffset {
  const param = options.param ?? DEFAULT_OFFSET_PARAM;
  const resetKey = options.resetKey;
  const sizeOpt = options.pageSize;
  const sizeParam = sizeOpt?.param ?? `${param}Size`;
  const [params, setParams] = useSearchParams();
  const offset = toOffset(params.get(param));
  const rawSize = sizeOpt === undefined ? null : Number(params.get(sizeParam));
  const size = sizeOpt === undefined ? undefined
    : (sizeOpt.options.includes(rawSize as number) ? (rawSize as number) : sizeOpt.defaultSize);

  // 첫 렌더에서는 되돌리지 않는다 — 링크로 받은 `?offset=` 을 지워 버리게 된다.
  const lastKey = useRef(resetKey);
  useEffect(() => {
    if (resetKey === undefined || lastKey.current === resetKey) return;
    lastKey.current = resetKey;
    setParams(
      (prev) => {
        if (!prev.has(param)) return prev;
        const next = new URLSearchParams(prev);
        next.delete(param);
        return next;
      },
      { replace: true },
    );
  }, [resetKey, param, setParams]);

  return {
    offset,
    goTo: (next: number) => {
      setParams((prev) => {
        const out = new URLSearchParams(prev);
        // 0 은 **기본값이라 URL 에 안 적는다.** 적으면 같은 화면의 주소가 둘이 된다.
        if (next <= 0) out.delete(param);
        else out.set(param, String(next));
        return out;
      });
    },
    ...(sizeOpt === undefined ? {} : {
      size,
      setSize: (next: number) => {
        // 한 setParams 호출 안에서 크기와 페이지를 같이 고친다 — 위 문단의 그 경쟁을 막는다.
        setParams((prev) => {
          const out = new URLSearchParams(prev);
          if (next === sizeOpt.defaultSize) out.delete(sizeParam);
          else out.set(sizeParam, String(next));
          out.delete(param);
          return out;
        });
      },
    }),
  };
}

/**
 * 목록의 **다른 축**도 URL 에 둔다 — 날짜·상태 필터처럼 페이지가 아닌 것들.
 *
 * `useListOffset` 과 같은 계약이다: 기본값은 URL 에 안 적는다(`null` 이 기본값이고,
 * 적으면 같은 화면의 주소가 둘이 된다). 훅을 따로 둔 이유는 되돌리기가 없기 때문이다 —
 * 이 값이 바뀌면 offset 을 0 으로 돌리는 것은 **부르는 쪽**이 `resetKey` 로 말한다.
 */
export function useUrlParam(name: string): readonly [string | null, (next: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(name);
  const set = (next: string | null) => {
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      if (next === null || next === '') out.delete(name);
      else out.set(name, next);
      return out;
    });
  };
  return [value, set] as const;
}

/**
 * 화면 하나를 **라우터 안에** 마운트한다. 시험이 쓰는 자리다.
 *
 * ─── 왜 SDK 가 이것을 내보내나 ───
 * 목록의 페이지는 URL 에 살고(`useListOffset`), 그래서 목록 화면은 `<Router>` 밖에서
 * 마운트되지 않는다 — 곧바로 던진다. 제품에서는 셸의 라우트 안이라 늘 있지만 시험은
 * 화면만 마운트한다.
 *
 * vertical 이 스스로 라우터를 세울 수는 **없다**: `react-router-only-in-core-web-and-sdk`
 * 가 막는다(0008 G5 — 손으로 만든 라우트는 표면 감사가 원리적으로 못 본다). 그 규칙을
 * 시험 편의로 뚫지 않고, **"화면을 마운트하는 데 필요한 것"의 소유자가 SDK** 라는 쪽으로
 * 답한다 — `SurfaceProvider`·`ModuleRpcProvider` 가 이미 그 목록에 있다.
 *
 * `createMemoryRouter` 다(`MemoryRouter` 가 아니라). 데이터 모드 라우터라야
 * `setSearchParams` 가 제품에서와 같은 경로로 돈다.
 */
export function StandaloneScreenRouter({ path, children }: {
  /** 시작 주소. 쿼리스트링을 붙이면 그 페이지에서 시작한다. */
  readonly path: string;
  readonly children: ReactNode;
}) {
  // 라우터는 항목이 바뀔 때만 다시 만든다 — 매 렌더 새로 만들면 상태가 초기화된다.
  const router = useMemo(
    () => createMemoryRouter([{ path: '*', element: children }], { initialEntries: [path] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path],
  );
  return <RouterProvider router={router} />;
}
